const PROVIDER_LABELS: Record<string, string> = {
  perplexity: "Perplexity",
  openrouter: "OpenRouter",
  mistral: "Mistral",
  firecrawl: "Firecrawl",
  pdl: "People Data Labs",
  hunter: "Hunter",
  serpapi: "SerpApi",
  elevenlabs: "ElevenLabs",
};

const QUOTA_TYPE_RE =
  /insufficient_quota|quota_exceeded|billing|payment_required|credits?_exhausted/i;
const QUOTA_MESSAGE_RE =
  /exceeded your current quota|insufficient.?quota|credits? exhausted|out of credits|payment required|quota exceeded/i;

export type ParsedProviderErrorBody = {
  message: string | null;
  type: string | null;
};

export function parseProviderErrorBody(
  body: string | null | undefined,
): ParsedProviderErrorBody {
  const raw = body?.trim() ?? "";
  if (!raw) return { message: null, type: null };

  try {
    const json: unknown = JSON.parse(raw);
    if (json && typeof json === "object") {
      const record = json as Record<string, unknown>;
      const nested =
        record.error && typeof record.error === "object"
          ? (record.error as Record<string, unknown>)
          : null;
      const message =
        readString(nested?.message) ??
        (typeof record.error === "string" ? record.error : null) ??
        readString(record.message);
      const type =
        readString(nested?.type) ??
        readString(nested?.code) ??
        readString(record.type) ??
        readString(record.code);
      return { message, type };
    }
  } catch {
    /* plain-text body */
  }

  return { message: raw.slice(0, 300), type: null };
}

export function isProviderQuotaError(
  httpStatus: number,
  body?: string | null,
): boolean {
  if (httpStatus === 402) return true;
  const parsed = parseProviderErrorBody(body);
  if (parsed.type && QUOTA_TYPE_RE.test(String(parsed.type))) return true;
  if (parsed.message && QUOTA_MESSAGE_RE.test(parsed.message)) return true;
  return false;
}

export class ProviderApiError extends Error {
  readonly provider: string;
  readonly httpStatus: number;
  readonly providerErrorType: string | null;
  readonly quotaExhausted: boolean;
  readonly bodySnippet: string | null;

  constructor(input: {
    provider: string;
    httpStatus: number;
    statusText: string;
    body?: string | null;
  }) {
    const parsed = parseProviderErrorBody(input.body);
    const quotaExhausted = isProviderQuotaError(input.httpStatus, input.body);
    const snippet = (parsed.message ?? input.body?.trim() ?? "").slice(0, 300);
    const label = providerLabel(input.provider);
    const typeTag = parsed.type ? ` [${parsed.type}]` : "";
    const detail = snippet ? ` — ${snippet}` : "";
    super(
      `${label} API error: ${input.httpStatus} ${input.statusText}${detail}${typeTag}`,
    );
    this.name = "ProviderApiError";
    this.provider = input.provider;
    this.httpStatus = input.httpStatus;
    this.providerErrorType = parsed.type;
    this.quotaExhausted = quotaExhausted;
    this.bodySnippet = snippet || null;
  }
}

export function findProviderApiError(err: unknown): ProviderApiError | null {
  let current: unknown = err;
  const seen = new Set<unknown>();
  while (current && !seen.has(current)) {
    seen.add(current);
    if (current instanceof ProviderApiError) return current;
    current = getErrorCause(current);
  }
  return null;
}

export type ProviderExceptionProperties = {
  provider?: string;
  httpStatus?: number;
  providerErrorType?: string;
  quotaExhausted?: boolean;
};

export function toProviderExceptionProperties(
  err: unknown,
): ProviderExceptionProperties {
  const providerError = findProviderApiError(err);
  if (!providerError) return {};
  return {
    provider: providerError.provider,
    httpStatus: providerError.httpStatus,
    ...(providerError.providerErrorType
      ? { providerErrorType: providerError.providerErrorType }
      : {}),
    quotaExhausted: providerError.quotaExhausted,
  };
}

export async function throwIfProviderHttpError(
  response: Response,
  provider: string,
): Promise<void> {
  if (response.ok) return;
  const body = await response.text().catch(() => "");
  throw new ProviderApiError({
    provider,
    httpStatus: response.status,
    statusText: response.statusText,
    body,
  });
}

function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? capitalize(provider);
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getErrorCause(err: unknown): unknown {
  if (!err || typeof err !== "object" || !("cause" in err)) return undefined;
  return (err as { cause?: unknown }).cause;
}

function readString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}
